import { useState, type ReactNode } from 'react'
import { useGlossary } from '../contexts/GlossaryContext'
import './ShareableStatement.css'

interface Props {
  id: string
  children: ReactNode
  /** 'paragraph' (default) | 'quote' | 'note' */
  type?: 'paragraph' | 'quote' | 'note'
  className?: string
}

/**
 * Inline-shareable content block. Shows a 🔗 link button on hover.
 * Copies `window.location.origin + pathname + '#' + id` to the clipboard.
 */
export default function ShareableStatement({ id, children, type = 'paragraph', className = '' }: Props) {
  const [status, setStatus] = useState<'idle' | 'copied'>('idle')
  const { t } = useGlossary()

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`
    navigator.clipboard.writeText(url).then(() => {
      setStatus('copied')
      setTimeout(() => setStatus('idle'), 2000)
    })
  }

  const Tag = type === 'quote' ? 'blockquote' : 'p'

  return (
    <Tag id={id} className={`shareable-stmt ss-${type} ${className}`.trim()}>
      <span className="ss-text">{children}</span>
      <button
        className="ss-link-btn"
    onClick={(e) => { e.stopPropagation(); copyLink(); }}
        title={t('shareableStatement.title')}
        aria-label={t('shareableStatement.aria')}
      >
        🔗
      </button>
      {status === 'copied' && (
        <span className="ss-toast" role="status">{t('shareableStatement.toast')}</span>
      )}
    </Tag>
  )
}
